
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, X, Save, Link as LinkIcon, Image as ImageIcon,
    Plus, ZoomIn, ZoomOut, Maximize2, Minimize2, Move
} from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';

interface Ad {
    id: number;
    type: 'image' | 'video';
    url: string;
    link?: string;
    ad_text?: string;
    ad_link?: string;
}

interface AdConfig {
    [key: string]: Ad[];
}

// Per-slot zoom / fit state (not saved to server — display-only for admin preview)
interface SlotDisplay {
    scale: number;       // 0.5 → 2.0
    objectFit: 'cover' | 'contain' | 'fill';
}

const defaultDisplay = (): SlotDisplay => ({ scale: 1, objectFit: 'cover' });

const FIT_LABELS: Record<SlotDisplay['objectFit'], string> = {
    cover: 'Crop Fill',
    contain: 'Fit Full',
    fill: 'Stretch',
};

export function AdManager() {
    const { showNotification } = useNotify();
    const [config, setConfig] = useState<AdConfig>({
        home: [],
        auctions: [],
        store: [],
        menu: [],
        full: []
    });
    const [activeTab, setActiveTab] = useState('home');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Display state per tab+index key e.g. "home-0"
    const [displayMap, setDisplayMap] = useState<Record<string, SlotDisplay>>({});

    const getDisplay = (tab: string, idx: number): SlotDisplay =>
        displayMap[`${tab}-${idx}`] || defaultDisplay();

    const setDisplay = (tab: string, idx: number, patch: Partial<SlotDisplay>) => {
        const key = `${tab}-${idx}`;
        setDisplayMap(prev => ({
            ...prev,
            [key]: { ...getDisplay(tab, idx), ...patch }
        }));
    };

    useEffect(() => {
        apiFetch('/api/ads/config')
            .then(res => res.json())
            .then(data => {
                setConfig({
                    home: data.home || [],
                    auctions: data.auctions || [],
                    store: data.store || [],
                    menu: data.menu || [],
                    full: data.full || []
                });
            })
            .catch(err => console.error(err));
    }, []);

    const handleUpload = async (file: File, index: number) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await apiFetch('/api/ads/upload', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (data.success) {
                const newAds = [...(config[activeTab] || [])];
                while (newAds.length <= index) {
                    newAds.push({ id: Date.now(), type: 'image', url: '' });
                }
                newAds[index] = {
                    ...newAds[index],
                    url: data.url,
                    type: data.type,
                    id: Date.now()
                };
                setConfig(prev => ({ ...prev, [activeTab]: newAds }));
                // Reset display to contain on new upload so nothing is clipped by default
                setDisplay(activeTab, index, { scale: 1, objectFit: 'contain' });
                showNotification('Success', 'Ad uploaded successfully!', 'success');
            } else {
                showNotification('Error', data.error || 'Upload failed', 'error');
            }
        } catch (e) {
            console.error('Upload failed', e);
            showNotification('Error', 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const updateField = (index: number, field: string, val: string) => {
        const newAds = [...config[activeTab]];
        if (newAds[index]) {
            // @ts-ignore
            newAds[index][field] = val;
            setConfig(prev => ({ ...prev, [activeTab]: newAds }));
        }
    };

    const removeAd = (index: number) => {
        const newAds = [...config[activeTab]];
        newAds.splice(index, 1);
        setConfig(prev => ({ ...prev, [activeTab]: newAds }));
    };

    const saveConfig = async () => {
        setLoading(true);
        try {
            await apiFetch('/api/ads/config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    page: activeTab,
                    ads: config[activeTab]
                })
            });
            showNotification('Success', 'Saved successfully!', 'success');
        } catch (e) {
            console.error(e);
            showNotification('Error', 'Save failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const currentAds = config[activeTab] || [];
    const slots = [...currentAds];
    if (slots.length < 3) {
        slots.push({ id: 0, type: 'image', url: '', ad_text: '', ad_link: '' });
    }

    return (
        <div className="bg-[#1c1c1e] rounded-3xl p-6 border border-white/5 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <ImageIcon className="text-blue-500" /> Ad Manager
                </h3>
                <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={saveConfig}
                    disabled={loading}
                    className="bg-blue-500 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50"
                >
                    <Save size={16} /> {loading ? 'Saving...' : 'Save Changes'}
                </motion.button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 noscroll">
                {['home', 'auctions', 'store', 'menu', 'full'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-full text-sm font-bold capitalize whitespace-nowrap transition-colors ${activeTab === tab
                            ? 'bg-white text-black'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                            }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Slots */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {slots.map((ad, idx) => {
                    const disp = getDisplay(activeTab, idx);

                    return (
                        <div key={ad.id || idx} className="bg-black/20 rounded-2xl p-4 border border-white/5 relative group space-y-3">

                            {ad.url ? (
                                <>
                                    {/* Delete button */}
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (confirm('Delete this ad?')) removeAd(idx);
                                        }}
                                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full z-20 shadow-lg active:scale-95 transition-all"
                                    >
                                        <X size={14} />
                                    </button>

                                    {/* ── Preview area ── */}
                                    <div className="aspect-video bg-black rounded-xl overflow-hidden relative border border-white/5">
                                        {ad.type === 'video' ? (
                                            <video
                                                src={ad.url}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: disp.objectFit,
                                                    transform: `scale(${disp.scale})`,
                                                    transformOrigin: 'center center',
                                                    transition: 'transform 0.2s ease, object-fit 0.1s',
                                                }}
                                                autoPlay muted loop
                                            />
                                        ) : (
                                            <img
                                                src={ad.url}
                                                alt="Ad preview"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: disp.objectFit,
                                                    transform: `scale(${disp.scale})`,
                                                    transformOrigin: 'center center',
                                                    transition: 'transform 0.2s ease, object-fit 0.1s',
                                                }}
                                            />
                                        )}

                                        {/* Type badge */}
                                        <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-0.5 rounded text-[9px] font-black uppercase text-white backdrop-blur-md z-10">
                                            {ad.type}
                                        </div>

                                        {/* Replace overlay */}
                                        <label className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors cursor-pointer z-10 flex items-center justify-center opacity-0 hover:opacity-100">
                                            <div className="bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] font-bold text-white flex items-center gap-1 border border-white/20">
                                                <Upload size={12} /> Replace
                                            </div>
                                            <input
                                                type="file"
                                                accept="image/*,video/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) handleUpload(e.target.files[0], idx);
                                                }}
                                            />
                                        </label>
                                    </div>

                                    {/* ── Zoom / Fit Controls ── */}
                                    <div className="bg-black/30 rounded-xl p-3 border border-white/5 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-black text-white/40 uppercase tracking-widest flex items-center gap-1">
                                                <Move size={10} /> Preview Zoom
                                            </span>
                                            <span className="text-[10px] font-mono font-bold text-blue-400">
                                                {Math.round(disp.scale * 100)}%
                                            </span>
                                        </div>

                                        {/* Zoom slider */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setDisplay(activeTab, idx, { scale: Math.max(0.5, parseFloat((disp.scale - 0.1).toFixed(1))) })}
                                                className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all flex-shrink-0"
                                                title="Zoom Out"
                                            >
                                                <ZoomOut size={13} className="text-white/60" />
                                            </button>

                                            <input
                                                type="range"
                                                min={50}
                                                max={200}
                                                step={5}
                                                value={Math.round(disp.scale * 100)}
                                                onChange={(e) => setDisplay(activeTab, idx, { scale: Number(e.target.value) / 100 })}
                                                className="flex-1 h-1.5 accent-blue-500 cursor-pointer"
                                            />

                                            <button
                                                onClick={() => setDisplay(activeTab, idx, { scale: Math.min(2, parseFloat((disp.scale + 0.1).toFixed(1))) })}
                                                className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all flex-shrink-0"
                                                title="Zoom In"
                                            >
                                                <ZoomIn size={13} className="text-white/60" />
                                            </button>
                                        </div>

                                        {/* Fit mode buttons */}
                                        <div className="flex gap-1.5">
                                            {(['cover', 'contain', 'fill'] as SlotDisplay['objectFit'][]).map(mode => (
                                                <button
                                                    key={mode}
                                                    onClick={() => setDisplay(activeTab, idx, { objectFit: mode })}
                                                    className={`flex-1 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all active:scale-95 ${disp.objectFit === mode
                                                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                                                        : 'bg-white/5 text-white/40 hover:bg-white/10 border border-white/5'
                                                        }`}
                                                >
                                                    {FIT_LABELS[mode]}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Reset */}
                                        <button
                                            onClick={() => setDisplay(activeTab, idx, defaultDisplay())}
                                            className="w-full text-[9px] text-white/20 hover:text-white/50 transition-colors font-medium"
                                        >
                                            Reset to Default
                                        </button>
                                    </div>
                                </>
                            ) : (
                                /* Empty slot — upload prompt */
                                <div className="aspect-video bg-white/5 rounded-xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center gap-2 mb-1 hover:bg-white/10 transition-colors relative cursor-pointer">
                                    <Plus className="text-white/30" size={24} />
                                    <span className="text-xs text-white/30 font-medium">Upload Creative</span>
                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        onChange={(e) => {
                                            if (e.target.files?.[0]) handleUpload(e.target.files[0], idx);
                                        }}
                                    />
                                </div>
                            )}

                            {/* Ad text + link fields */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/5 focus-within:border-blue-500/50 transition-colors">
                                    <ImageIcon size={13} className="text-gray-500 flex-shrink-0" />
                                    <input
                                        type="text"
                                        placeholder="Ad Title / Simple Text"
                                        className="bg-transparent w-full text-xs text-white placeholder:text-gray-600 focus:outline-none"
                                        value={ad.ad_text || ''}
                                        onChange={(e) => updateField(idx, 'ad_text', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/5 focus-within:border-blue-500/50 transition-colors">
                                    <LinkIcon size={13} className="text-gray-500 flex-shrink-0" />
                                    <input
                                        type="text"
                                        placeholder="Target Link (URL)"
                                        className="bg-transparent w-full text-xs text-white placeholder:text-gray-600 focus:outline-none"
                                        value={ad.ad_link || ad.link || ''}
                                        onChange={(e) => updateField(idx, 'ad_link', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <AnimatePresence>
                {uploading && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-2 text-xs text-blue-400"
                    >
                        <div className="w-3 h-3 border-2 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
                        Uploading media...
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
